import { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Assets
import UserOutlined from '@ant-design/icons/UserOutlined';
import BankOutlined from '@ant-design/icons/BankOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import FilePdfOutlined from '@ant-design/icons/FilePdfOutlined';
import FileImageOutlined from '@ant-design/icons/FileImageOutlined';
import SafetyCertificateOutlined from '@ant-design/icons/SafetyCertificateOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';

import AuthContext from 'contexts/AuthContext';
import axios from 'utils/axios';

// Componente para área de Upload de Ficheiros
function FileUploadBox({ label, helperText, accept, file, onFileSelect, onFileRemove, required = false }) {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      onFileSelect(e.target.files[0]);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const isPdf = file?.name?.toLowerCase().endsWith('.pdf');

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
        {label} {required && <span style={{ color: theme.palette.error.main }}>*</span>}
      </Typography>

      {!file ? (
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.grey[300]}`,
            borderRadius: 2,
            p: 2.5,
            textAlign: 'center',
            backgroundColor: isDragOver ? theme.palette.primary.lighter || '#f0f7ff' : theme.palette.grey[50],
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: '#f5f9ff'
            }
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <UploadOutlined style={{ fontSize: 28, color: theme.palette.primary.main, marginBottom: 8 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Clique ou arraste o ficheiro para aqui
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {helperText || 'PDF, PNG ou JPG até 10MB'}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            border: `1px solid ${theme.palette.success.light || '#b7eb8f'}`,
            borderRadius: 2,
            backgroundColor: '#f6ffed'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
            {isPdf ? (
              <FilePdfOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
            ) : (
              <FileImageOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            )}
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatSize(file.size)}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" color="error" onClick={onFileRemove}>
            <DeleteOutlined />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

FileUploadBox.propTypes = {
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  accept: PropTypes.string,
  file: PropTypes.object,
  onFileSelect: PropTypes.func.isRequired,
  onFileRemove: PropTypes.func.isRequired,
  required: PropTypes.bool
};

export default function AccountVerificationModal({ open, onClose }) {
  const theme = useTheme();
  const { user, setUser } = useContext(AuthContext);

  const [accountType, setAccountType] = useState('company');
  const [name, setName] = useState('');
  const [biNumber, setBiNumber] = useState('');
  const [nif, setNif] = useState('');

  // Files
  const [biFile, setBiFile] = useState(null);
  const [alvaraFile, setAlvaraFile] = useState(null);
  const [nifFile, setNifFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (user) {
      setAccountType(user.account_type || 'company');
      setName(user.name || '');
      setNif(user.nif || '');
      setBiNumber(user.bi_number || (user.account_type === 'individual' ? user.nif : ''));
    }
  }, [user, open]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (accountType === 'individual') {
      if (!biNumber.trim()) {
        setErrorMessage('Por favor, indique o número do Bilhete de Identidade.');
        return;
      }
      if (!biFile && !user?.documents?.bi) {
        setErrorMessage('É obrigatório anexar o ficheiro do Bilhete de Identidade (BI).');
        return;
      }
    } else {
      // Company
      if (!nif.trim()) {
        setErrorMessage('Por favor, indique o NIF da empresa.');
        return;
      }
      if (!alvaraFile && !user?.documents?.alvara) {
        setErrorMessage('É obrigatório anexar o ficheiro do Alvará Comercial.');
        return;
      }
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('account_type', accountType);
      formData.append('name', name);

      if (accountType === 'individual') {
        formData.append('bi_number', biNumber.trim());
        formData.append('nif', nif.trim() || biNumber.trim());
        if (biFile) {
          formData.append('bi_document', biFile);
        }
      } else {
        formData.append('nif', nif.trim());
        if (alvaraFile) {
          formData.append('alvara_document', alvaraFile);
        }
        if (nifFile) {
          formData.append('nif_document', nifFile);
        }
      }

      const response = await axios.post('/v1/merchant/verification', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUser(response.data.merchant);
      setSuccessMessage('Documentos submetidos com sucesso! A nossa equipa irá validar a sua conta.');

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Erro na validação:', err);
      const msg = err.response?.data?.message || err.message || 'Erro ao submeter documentação.';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ m: 0, p: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SafetyCertificateOutlined style={{ fontSize: 26, color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Validação de Conta
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Identificação e conformidade da conta comercial
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        <Stack spacing={2.5}>
          {user?.verification_status === 'rejected' && (
            <Alert severity="error">
              <strong>Submissão Rejeitada:</strong>{' '}
              {user.verification_rejection_reason || 'Por favor, reveja e reenvie os documentos corretos.'}
            </Alert>
          )}

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          {successMessage && (
            <Alert severity="success" icon={<CheckCircleOutlined />}>
              {successMessage}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              1. Selecione o Tipo de Conta
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Escolha a natureza jurídica da sua atividade:
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, width: '100%' }}>
              {/* Nome Individual */}
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  border: accountType === 'individual' ? `2px solid ${theme.palette.primary.main}` : undefined,
                  bgcolor: accountType === 'individual' ? '#f0f7ff' : '#fff',
                  borderRadius: 2
                }}
              >
                <CardActionArea onClick={() => setAccountType('individual')} sx={{ p: 1.5, height: '100%' }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <UserOutlined style={{ fontSize: 20, color: theme.palette.primary.main }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Nome Individual
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Para profissionais independentes e comerciantes em nome próprio.
                    </Typography>
                    <Chip label="Requer BI" size="small" color={accountType === 'individual' ? 'primary' : 'default'} />
                  </CardContent>
                </CardActionArea>
              </Card>

              {/* Empresa */}
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  border: accountType === 'company' ? `2px solid ${theme.palette.primary.main}` : undefined,
                  bgcolor: accountType === 'company' ? '#f0f7ff' : '#fff',
                  borderRadius: 2
                }}
              >
                <CardActionArea onClick={() => setAccountType('company')} sx={{ p: 1.5, height: '100%' }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BankOutlined style={{ fontSize: 20, color: theme.palette.primary.main }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Empresa
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Para sociedades comerciais, Lda, S.A. e entidades coletivas.
                    </Typography>
                    <Chip label="Requer Alvará e NIF" size="small" color={accountType === 'company' ? 'primary' : 'default'} />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
          </Box>

          <Divider />

          {/* Passo 2: Formulário Condicional */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              2. Dados e Submissão de Documentos
            </Typography>

            {accountType === 'individual' ? (
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Nome Completo"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome completo do titular"
                  required
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, width: '100%' }}>
                  <TextField
                    fullWidth
                    label="Nº do Bilhete de Identidade (BI)"
                    value={biNumber}
                    onChange={(e) => {
                      setBiNumber(e.target.value);
                      if (!nif) setNif(e.target.value);
                    }}
                    placeholder="Ex: 004567891LA042"
                    required
                    helperText="Conforme documento"
                  />
                  <TextField
                    fullWidth
                    label="NIF"
                    value={nif}
                    onChange={(e) => setNif(e.target.value)}
                    placeholder="Ex: 004567891LA042"
                    helperText="Geralmente o mesmo que o BI"
                  />
                </Box>

                <FileUploadBox
                  label="Bilhete de Identidade (BI)"
                  helperText="Frente e verso ou documento digital em PDF, PNG ou JPG (máx. 10MB)"
                  accept=".pdf,image/png,image/jpeg,image/jpg"
                  file={biFile}
                  onFileSelect={setBiFile}
                  onFileRemove={() => setBiFile(null)}
                  required={!user?.documents?.bi}
                />
                {user?.documents?.bi && !biFile && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: -1 }}>
                    ✓ Ficheiro de BI já enviado anteriormente. Selecione outro apenas se pretender substituir.
                  </Typography>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Denominação Social / Nome da Empresa"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Minha Empresa Lda"
                  required
                />
                <TextField
                  fullWidth
                  label="NIF da Empresa"
                  value={nif}
                  onChange={(e) => setNif(e.target.value)}
                  placeholder="Ex: 5412345678"
                  required
                />

                <FileUploadBox
                  label="Alvará Comercial"
                  helperText="Cópia do Alvará Comercial em vigor (PDF, PNG ou JPG até 10MB)"
                  accept=".pdf,image/png,image/jpeg,image/jpg"
                  file={alvaraFile}
                  onFileSelect={setAlvaraFile}
                  onFileRemove={() => setAlvaraFile(null)}
                  required={!user?.documents?.alvara}
                />
                {user?.documents?.alvara && !alvaraFile && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: -1 }}>
                    ✓ Alvará Comercial já enviado anteriormente. Selecione outro apenas se pretender substituir.
                  </Typography>
                )}

                <FileUploadBox
                  label="Comprovativo do NIF / Certidão Comercial (Opcional)"
                  helperText="Certidão de Registo Comercial ou Certidão de NIF"
                  accept=".pdf,image/png,image/jpeg,image/jpg"
                  file={nifFile}
                  onFileSelect={setNifFile}
                  onFileRemove={() => setNifFile(null)}
                />
                {user?.documents?.nif && !nifFile && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: -1 }}>
                    ✓ Documento de NIF já anexado anteriormente.
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Completar Mais Tarde
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SafetyCertificateOutlined />}
        >
          {loading ? 'A Enviar Documentos...' : 'Submeter para Validação'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AccountVerificationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};
